'use client';

import { useEffect, useSyncExternalStore } from 'react';

import { CoordinatesInterface } from 'types';

type PermissionState = 'granted' | 'denied' | 'prompt';

type GeoSnapshot = {
  geoLocation: CoordinatesInterface | null;
  permissionStatus: PermissionState | null;
};

const defaultSnapshot: GeoSnapshot = {
  geoLocation: null,
  permissionStatus: null
};

let snapshot: GeoSnapshot = { ...defaultSnapshot };
const listeners = new Set<() => void>();
let initStarted = false;

function emit() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getClientSnapshot(): GeoSnapshot {
  return snapshot;
}

function getServerSnapshot(): GeoSnapshot {
  return defaultSnapshot;
}

function startGeolocationOnce() {
  if (initStarted || typeof window === 'undefined') return;
  initStarted = true;

  const run = async () => {
    if (!('geolocation' in navigator)) return;

    let skipPosition = false;
    if ('permissions' in navigator) {
      try {
        const result = await navigator.permissions.query({
          name: 'geolocation'
        });
        snapshot = {
          ...snapshot,
          permissionStatus: result.state as PermissionState
        };
        emit();
        if (result.state === 'denied') skipPosition = true;
      } catch {
        // Permissions API unsupported or throws in some environments
      }
    }

    if (skipPosition) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        snapshot = {
          geoLocation: {
            longitude: position.coords.longitude,
            latitude: position.coords.latitude
          },
          permissionStatus: snapshot.permissionStatus ?? 'granted'
        };
        emit();
      },
      (error) => {
        const denied = error.code === error.PERMISSION_DENIED;
        snapshot = {
          ...snapshot,
          permissionStatus: denied
            ? 'denied'
            : snapshot.permissionStatus ?? 'prompt'
        };
        emit();
      }
    );
  };

  run().catch(() => undefined);
}

export const useGeolocation = () => {
  const state = useSyncExternalStore(
    subscribe,
    getClientSnapshot,
    getServerSnapshot
  );

  useEffect(() => {
    startGeolocationOnce();
  }, []);

  return {
    geoLocation: state.geoLocation,
    permissionStatus: state.permissionStatus
  };
};
